import React, { useCallback, useRef } from "react";
import { Label } from "../../../../../ui/label";
import { Input } from "../../../../../ui/input";
import { Textarea } from "../../../../../ui/textarea";
import { BENEFIT_TEMPLATE_VARIABLES } from "../../schemas";

/**
 * Input / textarea bound to a react-hook-form `Controller` `field`, with a
 * click-to-insert chip row of available template variables (see
 * core-lambda/.../template_renderer.py → build_render_context()).
 *
 * Clicking a chip inserts the {{ variable }} token at the current caret
 * position (or appends if the field hasn't been focused yet).
 */
const TemplateField = ({
  field,
  label,
  hint,
  error,
  multiline = false,
  rows = 4,
  maxLength,
  placeholder,
  variables = BENEFIT_TEMPLATE_VARIABLES,
}) => {
  const elRef = useRef(null);

  const setRef = useCallback(
    (node) => {
      elRef.current = node;
      if (typeof field?.ref === "function") field.ref(node);
    },
    [field]
  );

  const insertVariable = (token) => {
    const el = elRef.current;
    const current = field.value || "";
    if (!el) {
      field.onChange(current + token);
      return;
    }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.slice(0, start) + token + current.slice(end);
    field.onChange(next);
    requestAnimationFrame(() => {
      try {
        el.focus();
        el.setSelectionRange(start + token.length, start + token.length);
      } catch (_) {}
    });
  };

  const commonProps = {
    ref: setRef,
    value: field.value || "",
    onChange: (e) => field.onChange(e.target.value),
    onBlur: field.onBlur,
    name: field.name,
    maxLength,
    placeholder,
  };

  return (
    <div className="grid gap-1.5">
      <Label htmlFor={`tpl_${field.name}`}>{label}</Label>
      {multiline ? (
        <Textarea id={`tpl_${field.name}`} rows={rows} {...commonProps} />
      ) : (
        <Input id={`tpl_${field.name}`} {...commonProps} />
      )}
      <div className="flex flex-wrap gap-1.5">
        <span className="text-[11px] text-muted-foreground self-center">
          Insert:
        </span>
        {variables.map((v) => (
          <button
            key={v.token}
            type="button"
            onClick={() => insertVariable(v.token)}
            title={v.hint}
            className="rounded bg-muted text-[11px] px-1.5 py-0.5 hover:bg-muted/60 font-mono"
          >
            {v.token}
          </button>
        ))}
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};

export default TemplateField;
